import React, {useMemo} from 'react';
import {Stack, styled} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import NotificationBanner, {
  NotificationBannerState,
  NotificationContentType,
  type NotificationContent,
  type NotificationCustomRenderContent,
} from '@contentlab/components/notification-banner';
import {ERROR_CODE_MAP} from '@contentlab/config/errorCodeMap';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import {FeatureName} from '@contentlab/types/project';
import translate from '@/utils/translate';

interface AssetErrorsNotificationBannerProps {
  errorCodes: string[];
  maxMultipleErrors?: number;
  feature?: FeatureName | string;
}

const ErrorsListContainer = styled(Stack)(() => ({
  gap: '8px',
  paddingBottom: '8px',
  flex: 1,
  width: '100%',
  flexDirection: 'column',
}));

const ErrorsUnorderedList = styled('ul')(() => ({
  listStyle: 'none',
  margin: '0',
  padding: '0',
  color: designSystemToken('semantic.fg.danger'),
}));

function AssetErrorsNotificationBanner({
  errorCodes,
  maxMultipleErrors = 3,
  feature,
}: AssetErrorsNotificationBannerProps) {
  const uploadLimits = feature ? getUploadLimits(feature) : null;
  const translationParams = uploadLimits
    ? {
        maxSize: translate(uploadLimits.maxFileSizeDisplayKey),
        maxDuration: translate(uploadLimits.maxDurationDisplayKey),
      }
    : {};

  const errors = errorCodes
    .slice(0, maxMultipleErrors)
    .map((errorCode) => {
      return ERROR_CODE_MAP[errorCode];
    })
    .filter(Boolean);

  const content = useMemo(():
    | NotificationContent
    | NotificationCustomRenderContent => {
    if (errors.length === 0) {
      return {
        type: NotificationContentType.TITLE_AND_DESCRIPTION,
        title: '',
        description: '',
        inline: false,
      } as NotificationContent;
    }
    if (errors.length === 1) {
      const [error] = errors;
      return {
        type: NotificationContentType.TITLE_AND_DESCRIPTION,
        title: `${translate('Oops!')} ${translate(error.titleLocaleKey)}`,
        description: translate(error.descriptionLocaleKey, translationParams),
        inline: true,
      } as NotificationContent;
    }

    return {
      type: NotificationContentType.CUSTOM_RENDER,
      customRender: () => (
        <Stack>
          <Body size="sm" color={designSystemToken('semantic.fg.danger')}>
            <strong>{translate('Oops!')}</strong>{' '}
            {translate(
              'We could not upload this video due to multiple issues:'
            )}
          </Body>
          <ErrorsUnorderedList>
            {errors.map((error) => (
              <li key={error.descriptionLocaleKey}>
                <Body size="sm" color={designSystemToken('semantic.fg.danger')}>
                  <strong>{translate(error.titleLocaleKey)}</strong>{' '}
                  {translate(
                    error.shortDescriptionLocaleKey,
                    translationParams
                  )}
                </Body>
              </li>
            ))}
          </ErrorsUnorderedList>
        </Stack>
      ),
    } as NotificationCustomRenderContent;
  }, [errors, translationParams]);

  if (!errors.length) {
    return null;
  }

  return (
    <ErrorsListContainer>
      <NotificationBanner
        state={NotificationBannerState.ERROR}
        content={content}
      />
    </ErrorsListContainer>
  );
}

export default AssetErrorsNotificationBanner;
